/**
 * learn.4klabs.ai catalog API client.
 *
 * - Public reads/feedback use fetch (no auth), matching the manifest/review pattern.
 * - Admin curation uses the authenticated axios `api` instance (Firebase token).
 */

import api from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface CatalogItem {
  id: string;
  shareToken: string;
  conversationId: string;
  title: string | null;
  subject: string | null;
  grade: string | null;
  unit: string | null;
  /** Course placement — null while a lesson is curated but not yet in a class. */
  courseId: string | null;
  /** The section within the course: the subject. */
  chapter: string | null;
  order: number;
  featured: boolean;
  isPublished: boolean;
  allowFeedback: boolean;
  allowReviews: boolean;
  thumbnailUrl: string | null;
  slideCount: number;
  totalDuration: number;
  ratingCount: number;
  averageRating: number | null;
  updatedAt: string;
  /** Baked static CDN player bundle URL (cdn.4klabs.ai/vplay/…), or null if not baked. */
  staticBundleUrl?: string | null;
}

export interface SubmitFeedbackPayload {
  rating: number;
  reactions: string[];
  comment?: string | null;
  reviewerKey?: string | null;
}

// ============================================================================
// Public
// ============================================================================

export async function fetchCatalog(subject?: string | null): Promise<CatalogItem[]> {
  const qs = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  const res = await fetch(`${API_BASE_URL}/api/learn/catalog${qs}`);
  if (!res.ok) throw new Error('Failed to load catalog');
  const data = await res.json();
  return (data.items ?? []) as CatalogItem[];
}

export async function fetchCatalogItem(shareToken: string): Promise<CatalogItem | null> {
  const res = await fetch(`${API_BASE_URL}/api/learn/catalog/${shareToken}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to load experience');
  const data = await res.json();
  return (data.item ?? null) as CatalogItem | null;
}

export interface AssessmentSpec {
  assessmentId: string;
  questions: unknown[];
  [key: string]: unknown;
}

/**
 * The topic-test spec for a module, or null if none is published.
 * Served as a STATIC file from the frontend CDN (/assessments/<token>.json) — adding a
 * new module is just an S3 upload, no backend deploy. Falls back to the api endpoint.
 */
export async function fetchAssessment(shareToken: string): Promise<AssessmentSpec | null> {
  try {
    const res = await fetch(`/assessments/${shareToken}.json`, { cache: 'no-cache' });
    if (res.ok) return (await res.json()) as AssessmentSpec;
  } catch {
    /* fall through to api */
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/learn/${shareToken}/assessment`);
    if (!res.ok) return null;
    return (await res.json()) as AssessmentSpec;
  } catch {
    return null;
  }
}

/** Share tokens that have a published topic test — used to badge catalog cards. Static manifest. */
export async function fetchAssessmentTokens(): Promise<Set<string>> {
  try {
    const res = await fetch('/assessments/index.json', { cache: 'no-cache' });
    if (res.ok) {
      const data = await res.json();
      return new Set((data.shareTokens ?? []) as string[]);
    }
  } catch {
    /* fall through to api */
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/learn/assessments`);
    if (!res.ok) return new Set();
    const data = await res.json();
    return new Set((data.shareTokens ?? []) as string[]);
  } catch {
    return new Set();
  }
}

export interface SubmitFeedbackResult {
  success: boolean;
  feedbackId: string;
  averageRating: number;
  ratingCount: number;
}

export async function submitExperienceFeedback(
  shareToken: string,
  payload: SubmitFeedbackPayload,
): Promise<SubmitFeedbackResult> {
  const res = await fetch(`${API_BASE_URL}/api/learn/${shareToken}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw Object.assign(new Error(body?.message ?? 'Failed to submit feedback'), {
      status: res.status,
      code: body?.error ?? 'INTERNAL',
    });
  }
  return (await res.json()) as SubmitFeedbackResult;
}

export interface LinkedAssessment {
  shareToken: string;
  assessmentId: string | null;
  title: string | null;
  questionCount: number;
}

/**
 * New-framework interactive topic tests attached to this video (via
 * AssessmentShareLink.linkedVideoToken). Each opens the standalone runner by its
 * own share token. Empty array if none / on error.
 */
export async function fetchLinkedAssessments(shareToken: string): Promise<LinkedAssessment[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/learn/${shareToken}/assessments`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.assessments ?? []) as LinkedAssessment[];
  } catch {
    return [];
  }
}

// ============================================================================
// Admin (authenticated)
// ============================================================================

export interface UpsertExperiencePayload {
  conversationId?: string;
  shareToken?: string;
  subject?: string | null;
  grade?: string | null;
  unit?: string | null;
  order?: number;
  featured?: boolean;
  isPublished?: boolean;
  allowFeedback?: boolean;
}

export interface AdminLearner {
  id: string;
  email: string;
  displayName: string | null;
  photoUrl: string | null;
  actorId: string;
  status: 'PENDING' | 'ACTIVE' | 'DISABLED';
  /** ALL = sees the whole library; ASSIGNED = sees only what an admin ticked. */
  accessMode?: 'ALL' | 'ASSIGNED';
  /** How many grants are on the account (courses + lessons). */
  grantCount?: number;
  schoolId?: string | null;
  schoolName?: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  views: number;
  videos: number;
  completes: number;
  watchMs: number;
}

export async function adminListLearners(q?: string, schoolId?: string): Promise<AdminLearner[]> {
  const r = await api.get('/admin/learners', {
    params: { ...(q ? { q } : {}), ...(schoolId ? { schoolId } : {}) },
  });
  return (r.data.learners ?? []) as AdminLearner[];
}

/** A school and what the learners who joined with its code have done. */
export interface AdminSchool {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  grantCount: number;
  learners: number;
  activeLearners: number;
  lessonsOpened: number;
  completes: number;
  watchMs: number;
}

export async function adminListSchools(): Promise<AdminSchool[]> {
  const r = await api.get('/admin/learn/schools');
  return (r.data.schools ?? []) as AdminSchool[];
}

export async function adminCreateSchool(body: { name: string; code?: string }): Promise<void> {
  await api.post('/admin/learn/schools', body);
}

export async function adminUpdateSchool(
  id: string,
  patch: { name?: string; code?: string; isActive?: boolean },
): Promise<void> {
  await api.patch(`/admin/learn/schools/${id}`, patch);
}

export async function adminSetLearnerStatus(
  id: string,
  status: 'PENDING' | 'ACTIVE' | 'DISABLED',
): Promise<void> {
  await api.patch(`/admin/learners/${id}`, { status });
}

export async function adminListExperiences(): Promise<CatalogItem[]> {
  const r = await api.get('/admin/learn/experiences');
  return (r.data.items ?? []) as CatalogItem[];
}

export async function adminCreateExperience(body: UpsertExperiencePayload): Promise<CatalogItem> {
  const r = await api.post('/admin/learn/experiences', body);
  return r.data.item as CatalogItem;
}

export async function adminUpdateExperience(
  id: string,
  patch: Omit<UpsertExperiencePayload, 'conversationId' | 'shareToken'>,
): Promise<CatalogItem> {
  const r = await api.patch(`/admin/learn/experiences/${id}`, patch);
  return r.data.item as CatalogItem;
}

export async function adminDeleteExperience(id: string): Promise<void> {
  await api.delete(`/admin/learn/experiences/${id}`);
}

export interface FeedbackItem {
  id: string;
  rating: number;
  reactions: string[];
  comment: string | null;
  submittedAt: string;
}
export interface ExperienceFeedbackData {
  summary: { count: number; averageRating: number | null; reactions: Record<string, number> };
  items: FeedbackItem[];
}

export async function adminGetExperienceFeedback(id: string): Promise<ExperienceFeedbackData> {
  const r = await api.get(`/admin/learn/experiences/${id}/feedback`);
  return r.data as ExperienceFeedbackData;
}

// ── Class rosters (teacher-provisioned, minor-safe) ──────────────────────────

export interface SchoolClass {
  id: string;
  name: string;
  grade: string | null;
  joinCode: string;
  learnerCount?: number;
  createdAt: string;
}
export interface Learner {
  id: string;
  firstName: string;
  rollNo: string;
  learnerToken: string;
}

export async function adminListClasses(): Promise<SchoolClass[]> {
  const r = await api.get('/admin/learn/classes');
  return (r.data.classes ?? []) as SchoolClass[];
}
export async function adminCreateClass(body: { name: string; grade?: string }): Promise<SchoolClass> {
  const r = await api.post('/admin/learn/classes', body);
  return r.data.class as SchoolClass;
}
export async function adminGetClass(id: string): Promise<SchoolClass & { learners: Learner[] }> {
  const r = await api.get(`/admin/learn/classes/${id}`);
  return r.data.class as SchoolClass & { learners: Learner[] };
}
export async function adminAddLearners(id: string, raw: string): Promise<Learner[]> {
  const r = await api.post(`/admin/learn/classes/${id}/learners`, { raw });
  return (r.data.learners ?? []) as Learner[];
}
export async function adminDeleteLearner(id: string, learnerId: string): Promise<void> {
  await api.delete(`/admin/learn/classes/${id}/learners/${learnerId}`);
}
export async function adminDeleteClass(id: string): Promise<void> {
  await api.delete(`/admin/learn/classes/${id}`);
}

// ============================================================================
// Courses — Course (a class) → Chapter (a subject) → Lesson → Check
// ============================================================================

export interface CourseSummary {
  id: string;
  slug: string;
  gradeLevel: string | null;
  title: string;
  description: string | null;
  coverUrl: string | null;
  order: number;
  isPublished: boolean;
  lessonCount: number;
}

export interface CourseLesson {
  shareToken: string;
  title: string | null;
  chapter: string | null;
  order: number;
  thumbnailUrl: string | null;
  totalDuration: number;
  hasCheck: boolean;
}

export interface CourseDetail {
  course: CourseSummary;
  chapters: Array<{ title: string | null; lessons: CourseLesson[] }>;
}

export async function fetchCourses(): Promise<CourseSummary[]> {
  const res = await fetch(`${API_BASE_URL}/api/learn/courses`);
  if (!res.ok) throw new Error('Failed to load courses');
  return ((await res.json()).courses ?? []) as CourseSummary[];
}

export async function fetchCourse(courseId: string): Promise<CourseDetail | null> {
  const res = await fetch(`${API_BASE_URL}/api/learn/courses/${courseId}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to load course');
  return (await res.json()) as CourseDetail;
}

export async function adminListCourses(): Promise<CourseSummary[]> {
  const r = await api.get('/admin/learn/courses');
  return (r.data.courses ?? []) as CourseSummary[];
}

export async function adminUpsertCourse(body: {
  slug: string;
  gradeLevel?: string | null;
  title?: string;
  description?: string | null;
  order?: number;
  isPublished?: boolean;
}): Promise<CourseSummary> {
  const r = await api.post('/admin/learn/courses', body);
  return r.data.course as CourseSummary;
}

export async function adminPlaceLesson(
  learnExperienceId: string,
  placement: { courseId?: string | null; chapter?: string | null; order?: number; standardCodes?: string[] },
): Promise<void> {
  await api.patch(`/admin/learn/lessons/${learnExperienceId}/placement`, placement);
}
